import { snapLine } from "../overlays/grid";
import { createLineRenderer } from "../gl/lineRenderer";
import { createFillRenderer } from "../gl/fillRenderer";

// Levels are built directly in device pixels
const IDENTITY_TRANSFORM = [1, 0];
const LINE_COLOR_OFFSET = 4;
const FILL_BOTTOM_OFFSET = 4;
const FILL_COLOR_OFFSET = 6;

// One horizontal line per level across the canvas (the pane clip cuts it), whole device px wide
const buildLevelLines = ({ lines, toCssY, width, pixelRatio, floatsPerLine }) => {
  const data = new Float32Array(lines.length * floatsPerLine);

  lines.forEach(({ value, color, width: cssWidth }, index) => {
    const offset = index * floatsPerLine;
    const lineWidth = Math.max(1, Math.floor(cssWidth * pixelRatio));
    const y = snapLine(toCssY(value), pixelRatio, lineWidth);

    data.set([0, y, width, y], offset);
    data.set(color, offset + LINE_COLOR_OFFSET);
    data[offset + floatsPerLine - 1] = lineWidth;
  });

  return data;
};

// A rectangle between two levels across the canvas
const buildLevelFill = ({ fill, toCssY, width, pixelRatio, floatsPerInstance }) => {
  const data = new Float32Array(fill ? floatsPerInstance : 0);

  if (!fill) return data;

  const top = toCssY(fill.top) * pixelRatio;
  const bottom = toCssY(fill.bottom) * pixelRatio;

  data.set([0, top, width, top], 0);
  data.set([bottom, bottom], FILL_BOTTOM_OFFSET);
  data.set(fill.color, FILL_COLOR_OFFSET);

  return data;
};

/**
 * Levels of an oscillator in its pane, under its plots: a horizontal line
 * per visible level in its colour and width (RSI 70 / 50 / 30) and the background between the first
 * and the last level (CCI). A few instances, rebuilt every frame in device
 * px like the pane grid, so pan and the autoscale need nothing else.
 *
 * @param {object} glLayer
 */
export const createLevelRenderer = (glLayer) => {
  const lines = createLineRenderer();
  const fills = createFillRenderer();
  let levels = { lines: [], fill: null };

  [fills, lines].forEach((renderer) => glLayer.addRenderer(renderer));

  return {
    /** @param {{ lines: object[], fill: object|null }} nextLevels - shownPlots levels */
    setLevels: (nextLevels) => {
      levels = nextLevels;
    },

    /**
     * @param {object} params
     * @param {number[]} params.viewport - [width, height] of the canvas, device px
     * @param {object} params.priceScale - of the pane
     * @param {number} params.top - top of the pane in the chart, CSS px
     * @param {number} params.pixelRatio
     */
    draw: ({ viewport, priceScale, top, pixelRatio }) => {
      if (!levels.lines.length && !levels.fill) return;

      const toCssY = (value) => priceScale.priceToY(value) + top;
      const deviceTransforms = { viewport, xTransform: IDENTITY_TRANSFORM, yTransform: IDENTITY_TRANSFORM };
      const geometry = { toCssY, width: viewport[0], pixelRatio };

      fills.setInstances(
        buildLevelFill({ ...geometry, fill: levels.fill, floatsPerInstance: fills.floatsPerInstance }),
      );
      lines.setInstances(
        buildLevelLines({ ...geometry, lines: levels.lines, floatsPerLine: lines.floatsPerInstance }),
      );
      fills.draw(deviceTransforms);
      lines.draw(deviceTransforms);
    },

    release: () => {
      [fills, lines].forEach((renderer) => glLayer.removeRenderer(renderer));
    },
  };
};
